// src/js-dot/core/date/constants.ts
var DATE_LAST_DATES = [31, 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31];

// src/js-dot/core/date/is-leap-year.ts
function isLeapYear(date) {
  const y = date.getFullYear();
  return !(y % 4) && y % 100 || y % 400 === 0;
}

// src/js-dot/core/date/from-timestamp.ts
var _TIMESTAMP = /^(?:(\d{4})[-/.]?)?(?:(1[0-2]|0?[1-9])[-/.]?)?(3[01]|[12]\d|0?[1-9])(?:[\sT]?(2[0-3]|1\d|0?\d)(?::?([1-5]\d|0?\d)(?::?([1-5]\d|0?\d)(?:\.(\d+Z?)([+-]\d\d(?::\d\d)?)?)?)?)?)?$/;
function fromTimestamp(timestamp, forEnd) {
  switch (timestamp) {
    case null:
    case void 0:
    case "":
      return null;
  }
  const matched = timestamp.match(_TIMESTAMP);
  if (!matched) {
    throw RangeError("Invalid Timestamp Expression: " + timestamp);
  }
  const [_, y, m, d, h, i, s, u, t] = matched;
  const fixedM = m === void 0 ? !y ? (/* @__PURE__ */new Date()).getMonth() : forEnd ? 11 : 0 : +m - 1;
  return forEnd ? new Date(+y || (/* @__PURE__ */new Date()).getFullYear(), fixedM, +d, h ? +h : 23, i ? +i : 59, s ? +s : 59, u ? +u : 999) : new Date(+y || (/* @__PURE__ */new Date()).getFullYear(), fixedM, +d, h ? +h : 0, i ? +i : 0, s ? +s : 0, u ? +u : 0);
}

// src/js-dot/core/date/_digit-timestamp.ts
var _DIGIT_TIMESTAMP = /^([+-])?(\d{4})(?:(\d\d)(?:(\d\d)(?:(\d\d)(?:(\d\d)(?:(\d\d)(\d+)?)?)?)?)?)?(?:\.(\d+))?$/;

// src/js-dot/core/date/is-date-digit.ts
function isDateDigit(dateDigit) {
  switch (typeof dateDigit) {
    case "number":
    case "string":
      const matched = ("" + dateDigit).match(_DIGIT_TIMESTAMP);
      return matched || false;
  }
  return false;
}

// src/js-dot/core/date/from-date-digit.ts
import { getLastDate } from "@js-dot/core/date";
function fromDateDigit(digitExp_or_parts, forEnd) {
  const matched = Array.isArray(digitExp_or_parts) ? digitExp_or_parts : ("" + digitExp_or_parts).match(_DIGIT_TIMESTAMP);
  if (!matched) {
    throw RangeError(`Invalid Digit Timestamp Expression: ${digitExp_or_parts}`);
  }
  const [_, type, y, m, d, h, i, s, u, z] = matched;
  let date;
  if (forEnd) {
    const fixedM = m ? +m - 1 : 11;
    date = new Date(+y, fixedM, d ? +d : getLastDate(new Date(+y, fixedM)), h ? +h : 23, i ? +i : 59, s ? +s : 59, u ? +u.padEnd(3, "0") : 999);
  } else {
    date = new Date(+y, +m - 1 || 0, +d || 1, +h || 0, +i || 0, +s || 0, u ? +u.padEnd(3, "0") : 0);
  }
  if (z) {
    date = new Date(date.getTime() - date.getTimezoneOffset() * 6e4);
    const offset = +("" + z).padEnd(2, "0") * 36e5;
    return new Date(date.getTime() + (type === "-" ? offset : -offset));
  }
  return date;
}

// src/js-dot/core/date/get-date.ts
function getDate(dateExp, forEnd) {
  if (dateExp instanceof Date) {
    return dateExp;
  }
  if (typeof dateExp === "number") {
    if (+("" + dateExp).substring(0, 4) < (/* @__PURE__ */new Date()).getFullYear()) {
      return new Date(dateExp);
    }
  }
  const dateDigit = isDateDigit(dateExp);
  if (dateDigit) {
    return fromDateDigit(dateDigit, forEnd);
  }
  return fromTimestamp(dateExp, forEnd);
}

// src/js-dot/core/date/date-modify.ts
var _FIXERS = /([+-]\s*)?(\d*)\s*(min(?:utes?)?|y(?:ears?)?|m(?:on(?:ths?)?)?|d(?:ays?)?|h(?:ours?)?|i|s(?:ec(?:onds?)?)?)/ig;
var _FIXER = /([+-]\s*)?(\d*)([^\d]{1,2})/;
function dateModify(format, date = /* @__PURE__ */new Date()) {
  const time = new Date(date.getTime());
  let seconds = 0;
  const matched = format.match(_FIXERS);
  if (!matched) {
    return time;
  }
  const end = matched.length;
  let current = -1;
  while (++current < end) {
    const split = matched[current].match(_FIXER);
    const value = +(split[1] === "-" ? -(split[2] || 1) : split[2] || 1);
    if (!value) {
      return void 0;
    }
    const unit = split[3];
    switch (unit) {
      case "y":
      case "ye":
        time.setFullYear(time.getFullYear() + value);
        break;
      case "m":
      case "mo":
        const months = time.getMonth() + value;
        if (months < 12) {
          time.setMonth(months);
        } else {
          time.setFullYear(time.getFullYear() + Math.ceil(months / 12));
          time.setMonth(months % 12);
        }
        break;
      case "d":
      case "da":
        seconds += value * 86400;
        break;
      case "h":
      case "ho":
        seconds += value * 3600;
        break;
      case "i":
      case "mi":
        seconds += value * 60;
        break;
      case "s":
      case "se":
        seconds += value;
        break;
    }
  }
  return new Date(time.getTime() + seconds * 1e3);
}

// src/js-dot/core/date/date-parts.ts
var DateParts = class {
  y;
  m;
  d;
  h;
  i;
  s;
  u;
  t;
  constructor(date = /* @__PURE__ */new Date()) {
    this.y = ("" + date.getFullYear()).padStart(4, "0");
    this.m = ("" + (date.getMonth() + 1)).padStart(2, "0");
    this.d = ("" + date.getDate()).padStart(2, "0");
    this.h = ("" + date.getHours()).padStart(2, "0");
    this.i = ("" + date.getMinutes()).padStart(2, "0");
    this.s = ("" + date.getSeconds()).padStart(2, "0");
    this.u = ("" + date.getMilliseconds()).padStart(3, "0");
    const t = date.getTimezoneOffset();
    this.t = t ? t / -60 : 0;
  }
};

// src/js-dot/core/date/date-format.ts
var {
  max: MAX
} = Math;
var SUFFIXES = [null, "st", "nd", "rd", "th"];
var LAST_DATE = [31, 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31];
var LOWER_MERIDIEMS = ["am", "pm", "am"];
var UPPER_MERIDIEMS = ["AM", "PM", "AM"];
var DAYS = ["Sunday", "Monday", "Tuesday", "Wednesday", "Friday", "Saturday"];
var MONTHS = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];
var DateFormat = class _DateFormat {
  //////////////////////////////
  // DAYS
  //////////////////////////////
  /**
   * Day of the month, 2 digits with leading zeros
   * 01 to 31
   * @param date
   */
  static d(date) {
    return zf(date.getDate());
  }
  /**
   * A textual representation of a day, three letters
   * @param date
   */
  static D(date) {
    return DAYS[date.getDay()].substring(0, 3);
  }
  /**
   * Day of the month without leading zeros
   * 1 to 31
   * @param date
   */
  static j(date) {
    return date.getDate();
  }
  /**
   *    A full textual representation of the day of the week
   *    Sunday through Saturday
   * @param date
   */
  static l(date) {
    return DAYS[date.getDay()];
  }
  /**
   * ISO-8601 numeric representation of the day of the week
   * 1 (for Monday) through 7 (for Sunday)
   * @param date
   */
  static N(date) {
    return date.getDate() || 7;
  }
  /**
   * English ordinal suffix for the day of the month, 2 characters
   * st, nd, rd or th. Works well with j
   * @param date
   */
  static S(date) {
    return SUFFIXES[MAX(4, date.getDate())];
  }
  /**
   * Numeric representation of the day of the week
   * 0 (for Sunday) through 6 (for Saturday)
   * @param date
   */
  static w(date) {
    return date.getDay();
  }
  /**
   * The day of the year (starting from 0)
   * @param date
   */
  static z(date) {
    const m = date.getMonth();
    let currentMonthDays = date.getDate();
    if (m) {
      currentMonthDays += DATE_LAST_DATES[m];
      if (m > 1) {
        currentMonthDays += _DateFormat.L(date);
      }
    }
    return currentMonthDays;
  }
  //////////////////////////////
  // WEEK
  //////////////////////////////
  /**
   * ISO 8601 week number of year, weeks starting on Monday
   * Example: 42 (the 42nd week in the year)
   */
  static W(date) {
    const firstDate = new Date(date.getFullYear(), 0, 1);
    return Math.ceil(_DateFormat.z(date) + firstDate.getDay()) / 7;
  }
  //////////////////////////////
  // MONTH
  //////////////////////////////
  /**
   *    A full textual representation of a month, such as January or March
   *  January through December
   * @param date
   * @constructor
   */
  static F(date) {
    return MONTHS[date.getMonth()];
  }
  /**
   * Numeric representation of a month, with leading zeros
   *    01 through 12
   * @param date
   */
  static m(date) {
    return zf(date.getMonth() + 1);
  }
  /**
   *    A short textual representation of a month, three letters
   *    Jan through Dec
   * @param date
   * @constructor
   */
  static M(date) {
    return MONTHS[date.getMonth()].substring(0, 3);
  }
  /**
   * Numeric representation of a month, without leading zeros
   *    1 through 12
   * @param date
   */
  static n(date) {
    return date.getMonth() + 1;
  }
  /**
   * Number of days in the given month
   *    28 through 31
   * @param date
   */
  static t(date) {
    const m = date.getMonth();
    return LAST_DATE[m] + (m === 1 ? _DateFormat.L(date) : 0);
  }
  //////////////////////////////
  // YEAR
  //////////////////////////////
  /**
   * Whether it's a leap year
   * 1 if it is a leap year, 0 otherwise.
   * @param date
   */
  static L(date) {
    return isLeapYear(date) ? 1 : 0;
  }
  /**
   * ISO-8601 week-numbering year. This has the same value as Y, except that if the ISO week number (W) belongs to the previous or next year, that year is used instead.
   * Examples: 1999 or 2003
   */
  static o(date) {
    return date.getFullYear();
  }
  /**
   * A full numeric representation of a year, 4 digits
   *    Examples: 1999 or 2003
   */
  static Y(date) {
    return date.getFullYear();
  }
  /**
   * A two digit representation of a year
   * Examples: 99 or 03
   */
  static y(date) {
    return ("" + date.getFullYear()).slice(2);
  }
  //////////////////////////////
  // Time
  //////////////////////////////
  /**
   * Uppercase Ante meridiem and Post meridiem
   *    am or pm
   */
  static a(date) {
    return LOWER_MERIDIEMS[Math.floor(date.getHours() / 12)];
  }
  /**
   * Lowercase Ante meridiem and Post meridiem
   *    AM or PM
   */
  static A(date) {
    return UPPER_MERIDIEMS[Math.floor(date.getHours() / 12)];
  }
  // B, what is it internet time?
  /**
   * 12-hour format of an hour without leading zeros
   *    1 through 12
   */
  static g(date) {
    return Math.floor(date.getHours() % 12);
  }
  /**
   * 24-hour format of an hour without leading zeros
   *    0 through 23
   */
  static G(date) {
    return date.getHours();
  }
  /**
   * 12-hour format of an hour with leading zeros
   *    01 through 12
   */
  static h(date) {
    return zf(Math.floor(date.getHours() % 12));
  }
  /**
   * 24-hour format of an hour with leading zeros
   *    00 through 23
   */
  static H(date) {
    return zf(date.getHours());
  }
  /**
   * Minutes with leading zeros
   *    00 to 59
   */
  static i(date) {
    return zf(date.getMinutes());
  }
  /**
   * Seconds with leading zeros
   *    00 through 59
   */
  static s(date) {
    return zf(date.getSeconds());
  }
  /**
   * Microseconds
   * @example 654321
   */
  static u(date) {
    return date.getMilliseconds();
  }
  /**
   * Milliseconds. Same note applies as for u.
   * @param date
   *    Example: 654
   */
  static v(date) {
    return date.getMilliseconds();
  }
  //////////////////////////////
  // Timezone
  //////////////////////////////
  // todo: e
  // todo: I
  // todo: O
  // todo: P
  // todo: T
  // todo: Z
  //////////////////////////////
  // Full Date/Time
  //////////////////////////////
  /**
   * ISO 8601 date
   * @example    2004-02-12T15:19:21+00:00
   * @param date
   */
  static c(date) {
    const {
      y,
      m,
      d,
      h,
      i,
      s,
      t
    } = new DateParts(date);
    return `${y}-${m}-${d}T${h}:${i}:${s}${t > -1 ? "+" : "-"}${t}:00`;
  }
  // todo: r
  // todo: U
};
function zf(value) {
  return value < 10 ? "0" + value : value;
}
function dateFormat(format, date = /* @__PURE__ */new Date()) {
  if (!(date instanceof Date)) {
    date = new Date(date);
  }
  const end = format.length;
  let current = -1;
  let char;
  let converted = "";
  while (++current < end) {
    char = format.charAt(current);
    converted += DateFormat[char] ? DateFormat[char](date) : char;
  }
  return converted;
}

// src/js-dot/core/date/date-formatted.ts
function dateFormatted(format, date = /* @__PURE__ */new Date()) {
  return new Date(dateFormat(format, date));
}

// src/js-dot/core/date/to-date-digit.ts
import { zerofill } from "@js-dot/core";
function toDateDigit(date = /* @__PURE__ */new Date()) {
  if (!(date instanceof Date)) {
    date = new Date(date);
  }
  const {
    y,
    m,
    d,
    h,
    i,
    s,
    u,
    t
  } = new DateParts(date);
  const su = +s + +u;
  const isu = +i + +su;
  const time = y + m + d + (+h + isu ? h + (isu ? i + (su ? s + +u : "") : "") : "");
  return t ? t < 0 ? "-" + time + "." + zerofill(-t) : time + "." + zerofill(t) : time;
}

// src/js-dot/core/date/get-last-date.ts
function getLastDate2(date) {
  const month = date.getMonth();
  return month === 1 ? isLeapYear(date) ? 29 : 28 : DATE_LAST_DATES[month];
}

// src/js-dot/core/date/period.ts
var PERIOD_GLUE = "~";
var Period = class _Period {
  #duration;
  #durationSeconds;
  #days;
  begin;
  end;
  static from(begin_or_periodExp, end) {
    if (begin_or_periodExp instanceof _Period) {
      return begin_or_periodExp;
    }
    if (end instanceof _Period) {
      throw new RangeError("the `end` argument cannot be Period Instance");
    }
    return arguments.length === 1 ? new _Period(begin_or_periodExp) : new _Period(begin_or_periodExp, end);
  }
  static timestamp(begin, end) {
    return _Period.from(begin, end).timestamp();
  }
  get duration() {
    return this.#duration || (this.#duration = this.end.getTime() - this.begin.getTime());
  }
  get durationSeconds() {
    return this.#durationSeconds || (this.#durationSeconds = this.duration / 1e3);
  }
  get days() {
    return this.#days || (this.#days = this.durationSeconds / 86400);
  }
  constructor(begin_or_periodExp, end) {
    let begin = begin_or_periodExp;
    switch (typeof begin) {
      case "string":
        switch (begin) {
          case "yesterday":
            const yesterday = dateModify("-1d");
            begin = dateFormatted("Y-m-d 00:00:00.000", yesterday);
            end = dateFormatted("Y-m-d 23:59:59.999", yesterday);
            break;
          case "today":
            begin = dateFormatted("Y-m-d 00:00:00.000");
            end = dateFormatted("Y-m-d 23:59:59.999");
            break;
          case "tomorrow":
            const tomorrow = dateModify("+1d");
            begin = dateFormatted("Y-m-d 00:00:00.000", tomorrow);
            end = dateFormatted("Y-m-d 23:59:59.999", tomorrow);
            break;
          case "week":
            begin = dateFormatted("Y-m-d 00:00:00.000", dateModify("-7d"));
            end = dateFormatted("Y-m-d 23:59:59.999");
            break;
          case "month":
            begin = dateFormatted("Y-m-1 00:00:00.000");
            end = dateFormatted(`Y-m-${getLastDate2(begin)} 23:59:59.999`);
            break;
          case "now-month":
            begin = dateFormatted("Y-m-1 00:00:00.000");
            end = /* @__PURE__ */new Date();
            break;
          default:
            if (arguments.length === 1) {
              if (!begin.includes("~")) {
                throw RangeError("Period Required `~` Separator in Period string expression");
              }
              const [beginString, endString] = begin.split(PERIOD_GLUE);
              begin = beginString ? getDate(beginString) : /* @__PURE__ */new Date();
              end = endString ? getDate(endString, true) : /* @__PURE__ */new Date();
            } else {
              if (!begin && !end) {
                throw RangeError("Period Required `begin` date or `end` date in Separated Date.");
              }
              begin = begin ? getDate(begin) : null;
              end = end ? getDate(end) : null;
            }
        }
        break;
      case "number":
        begin = new Date(begin);
        break;
      default:
        if (begin instanceof _Period) {
          this.begin = begin.begin;
          this.end = begin.end;
          return;
        }
    }
    this.begin = begin;
    this.end = end;
  }
  /**
   * Get Period String
   * `±YYYYYY-MM-DDTHH:mm:ss. sss~±YYYYYY-MM-DDTHH:mm:ss. sss`
   */
  timestamp() {
    const {
      begin,
      end
    } = this;
    return (begin ? this.begin.toISOString() : "") + "~" + (end ? this.end.toISOString() : "");
  }
  /**
   * Get Period String as Digit Timestamp Expression
   * `YYYYMMDDHHIISSUUUU.Z~YYYYMMDDHHIISSUUUU.Z`
   */
  toDigit() {
    const {
      begin,
      end
    } = this;
    return begin || end ? (begin ? toDateDigit(this.begin) : "") + "~" + (end ? toDateDigit(this.end) : "") : "";
  }
  toString() {
    return this.toDigit();
  }
  // /**
  //  * split the period to past and future period
  //  * @param separator
  //  */
  // split(separator?: DateLike): PeriodSegments;
  // split(separator = new Date) {
  // 	separator = dateFormatted('Y-m-dTH:00:00', separator)
  //
  // 	const {begin, end} = this;
  // 	if (begin < separator) {
  // 		const past = new Period(begin, separator);
  // 		if (end > separator) {
  // 			return {
  // 				past,
  // 				future: new Period(separator, end)
  // 			};
  // 		}
  // 		return {
  // 			past
  // 		};
  // 	}
  // 	return {
  // 		future: this
  // 	};
  // }
  /**
   * Get separated Periods by microseconds as gap
   *
   * @example Periods by Date
   * const period = new Period(
   * 	new Date(2025, 10, 1),
   * 	new Date(2025, 10, 30)
   * );
   *
   * // 2025-11-01 ~ 2025-11-01-23:59:59
   * // 2025-11-02 ~ 2025-11-02-23:59:59
   * // 2025-11-03 ~ 2025-11-03-23:59:59
   * // ~
   * // 2025-11-30 ~ 2025-11-30-23:59:59
   * const periods = period.split(86400000);
   *
   * @param microseconds
   */
  split(microseconds) {
    const {
      begin,
      end
    } = this;
    const periods = [];
    let current = begin;
    let next;
    do {
      next = new Date(current.getTime() + microseconds);
      if (next > end) {
        next = end;
      }
      periods[periods.length] = new _Period(new Date(current), next);
      current = next;
    } while (next < end);
    return periods;
  }
};

// src/js-dot/core/date/get-dates.ts
var METHODS = {
  year: "Year",
  month: "Month",
  day: "Date",
  hour: "Hours",
  minute: "Minutes",
  second: "Seconds"
};
function getDates(periodExpression_or_period, unit) {
  const period = Period.from(periodExpression_or_period);
  const {
    begin,
    end
  } = period;
  const result = [];
  let current = begin;
  const method = METHODS[unit];
  const getter = "get" + method;
  const setter = "set" + method;
  while (current <= end) {
    result[result.length] = new Date(current);
    current[setter](current[getter]() + 1);
  }
  return result;
}

// src/js-dot/core/date/to-ymd-digit.ts
function toYmdDigit(date = /* @__PURE__ */new Date()) {
  return toDateDigit(dateFormatted("Y-m-d 00:00:00.0", date));
}

// src/js-dot/core/date/dates.ts
var SUFFIXES2 = [null, "st", "nd", "rd"];
var LAST_DATE2 = [31, 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31];
var LAST_DATE_DAYS = [31, 59, 90, 120, 151, 181, 212, 243, 273, 304, 334, 365];
var LOWER_APM = ["am", "pm", "am"];
var UPPER_APM = ["AM", "PM", "AM"];
var DAY_TIME = 864e5;
var _FIXERS2 = /([+-]\s*)?(\d*)\s*(min(?:utes?)?|y(?:ears?)?|m(?:on(?:ths?)?)?|d(?:ays?)?|h(?:ours?)?|i|s(?:ec(?:onds?)?)?)/ig;
var _FIXER2 = /([+-]\s*)?(\d*)([^\d]{1,2})/;
var _DELIMITER = /\d+[.:\-/]\d+/;
var Dates = class _Dates extends Date {
  static MONTHS = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12];
  static isLike(target) {
    return target instanceof _Dates || target instanceof Date || !!String(target).match(_DELIMITER) && new Date(target).toString() !== "Invalid Date";
  }
  /**
   * get dates from Dates, Date and date-able values like number, string
   * @param date
   */
  static by(date) {
    if (date instanceof _Dates) {
      return date;
    }
    if (date instanceof Date) {
      return new _Dates(date.getTime());
    }
    return date ? new _Dates(date) : new _Dates();
  }
  static yesterday(date = new _Dates()) {
    return _Dates.by(date).yesterday();
  }
  static tomorrow(date = new _Dates()) {
    return _Dates.by(date).tomorrow();
  }
  static firstTime(date = new _Dates()) {
    return _Dates.by(date).firstTime();
  }
  static onTime(date = new _Dates()) {
    return _Dates.by(date).onTime();
  }
  static lastTime(date = new _Dates()) {
    return _Dates.by(date).lastTime();
  }
  static firstDate(date = new _Dates()) {
    return _Dates.by(date).firstDate();
  }
  static lastDate(date = new _Dates()) {
    return _Dates.by(date).lastDate();
  }
  static isLeapYear(date = new _Dates()) {
    const y = _Dates.y(date);
    return !(y % 4) && y % 100 || y % 400 === 0;
  }
  static dates(date = new _Dates()) {
    if (typeof date === "number" && date < 13) {
      return LAST_DATE2[date - 1];
    }
    return _Dates.by(date).dates();
  }
  static modify(format, date = new _Dates()) {
    return _Dates.by(date).modify(format);
  }
  static format(format, date = new _Dates()) {
    const end = format.length;
    let current = -1;
    let char;
    let converted = "";
    while (++current < end) {
      char = format.charAt(current);
      converted += _Dates[char] ? _Dates[char](date) : char;
    }
    return converted;
  }
  static formatted(format, date = new _Dates()) {
    return _Dates.by(date).formatted(format);
  }
  static timestamp(date = new _Dates()) {
    return _Dates.by(date).timestamp();
  }
  static S(date = new _Dates()) {
    const value = _Dates.by(date).getDate();
    return value < 4 ? SUFFIXES2[value] : "th";
  }
  static G(date = new _Dates()) {
    return _Dates.by(date).getHours() % 12;
  }
  static g(date = new _Dates()) {
    return Math.floor(_Dates.by(date).getHours() % 12);
  }
  static a(date = new _Dates()) {
    return LOWER_APM[Math.floor(_Dates.by(date).getHours() / 12)];
  }
  static y(date = new _Dates()) {
    return String(_Dates.by(date).getFullYear()).slice(2);
  }
  static Y(date = new _Dates()) {
    return _Dates.by(date).getFullYear();
  }
  static A(date = new _Dates()) {
    return UPPER_APM[Math.floor(_Dates.by(date).getHours() / 12)];
  }
  static L(date = new _Dates()) {
    return _Dates.isLeapYear(date) ? 1 : 0;
  }
  static z(date = new _Dates()) {
    date = _Dates.by(date);
    let days = date.getDate();
    const m = date.getMonth();
    if (m) {
      days += LAST_DATE_DAYS[m - 1];
      if (m > 1) {
        days += _Dates.L(date);
      }
    }
    return days;
  }
  static t(date = new _Dates()) {
    return _Dates.dates(date);
  }
  static n(date = new _Dates()) {
    return _Dates.by(date).getMonth() + 1;
  }
  static m(date = new _Dates()) {
    return zerofill_2(_Dates.by(date).getMonth() + 1);
  }
  static w(date = new _Dates()) {
    return _Dates.by(date).getDay();
  }
  static o(date = new _Dates()) {
    return _Dates.by(date).getFullYear();
  }
  static h(date = new _Dates()) {
    return zerofill_2(_Dates.g(date));
  }
  static H(date = new _Dates()) {
    return zerofill_2(_Dates.by(date).getHours());
  }
  static i(date = new _Dates()) {
    return zerofill_2(_Dates.by(date).getMinutes());
  }
  static s(date = new _Dates()) {
    return zerofill_2(_Dates.by(date).getSeconds());
  }
  static v(date = new _Dates()) {
    return _Dates.by(date).getMilliseconds();
  }
  static u(date = new _Dates()) {
    return _Dates.by(date).getMilliseconds();
  }
  static d(date = new _Dates()) {
    return zerofill_2(_Dates.by(date).getDate());
  }
  static j(date = new _Dates()) {
    return _Dates.by(date).getDate();
  }
  static N(date = new _Dates()) {
    return _Dates.by(date).getDay() || 7;
  }
  //////////////////////////////
  // prototypes
  /**
   * get yesterday dates
   */
  yesterday() {
    return _Dates.by(this.firstTime().getTime() - DAY_TIME);
  }
  /**
   * get tomorrow dates
   */
  tomorrow() {
    return _Dates.by(this.firstTime().getTime() + DAY_TIME);
  }
  /**
   * get dates as first time of this date
   * @description
   *   Y-m-d 00:00:00
   */
  firstTime() {
    return this.formatted("Y-m-dT00:00:00");
  }
  /**
   * get dates at the given time as on time.
   */
  onTime() {
    return this.formatted("Y-m-dTH:00:00");
  }
  /**
   * get dates as last time of this date
   * @description
   *   Y-m-d 23:59:59
   */
  lastTime() {
    return this.formatted("Y-m-dT23:59:59");
  }
  /**
   * get dates as first date time of given month
   * @description
   *   Y-m-d 00:00:00
   */
  firstDate() {
    return this.formatted("Y-m-01T00:00:00");
  }
  /**
   * get dates as last date time of given month
   * @description
   *   Y-m-L 23:59:59
   */
  lastDate() {
    return this.formatted("Y-m-tT23:59:59");
  }
  /**
   * @alias Dates.L
   */
  isLeapYear() {
    return this.L();
  }
  /**
   * get last dates of given month
   */
  dates() {
    const m = this.getMonth();
    return LAST_DATE2[m] + (m === 1 ? this.L() : 0);
  }
  /**
   * @deprecated
   */
  timestamp() {
    return this.format("Y-m-d H:i:s");
  }
  /**
   * @deprecated
   */
  modify(format) {
    const time = new _Dates(this.getTime());
    let seconds = 0;
    const matched = format.match(_FIXERS2);
    if (!matched) {
      return time;
    }
    matched.forEach(command => {
      const split = command.match(_FIXER2);
      const value = Number(split[1] === "-" ? -(split[2] || 1) : split[2] || 1);
      if (!value) {
        return;
      }
      const unit = split[3];
      switch (unit) {
        case "y":
        case "ye":
          time.setFullYear(time.getFullYear() + value);
          break;
        case "m":
        case "mo":
          const months = time.getMonth() + value;
          if (months < 12) {
            time.setMonth(months);
          } else {
            time.setFullYear(time.getFullYear() + Math.ceil(months / 12));
            time.setMonth(months % 12);
          }
          break;
        case "d":
        case "da":
          seconds += value * 86400;
          break;
        case "h":
        case "ho":
          seconds += value * 3600;
          break;
        case "i":
        case "mi":
          seconds += value * 60;
          break;
        case "s":
        case "se":
          seconds += value;
          break;
      }
    });
    return new _Dates(time.getTime() + seconds * 1e3);
  }
  format(format) {
    return _Dates.format(format, this);
  }
  formatted(format) {
    return _Dates.by(this.format(format));
  }
  /**
   * Day of the month, 2 digits with leading zeros
   */
  d() {
    return zerofill_2(this.getDate());
  }
  /*
  static D(date: Target){
    return date.getDate();
  }
  D(){
    return date.getDate();
  }
  */
  /**
   * Day of the month without leading zeros
   */
  j() {
    return this.getDate();
  }
  /*
  	static l(date:Target){
  
  	}
  	l(){
  
  	}
  	*/
  /**
   * ISO-8601 numeric representation of the day of the week
   */
  N() {
    return this.getDay() || 7;
  }
  /**
   * English ordinal suffix for the day of the month, 2 characters
   */
  S() {
    return _Dates.S(this);
  }
  /**
   * Numeric representation of the day of the week
   */
  w() {
    return this.getDay();
  }
  /**
   * ISO-8601 week number of year, weeks starting on Monday
   * @param date
   */
  /*
  static W(date: Target) {
   
  }
   
  W() {
    return Dates.W(this);
  }
  */
  /**
   * A full textual representation of a month, such as January or March
   * @param date
   */
  /*
  static F(date: Target){
   
  }
  F(){
    return Dates.F(this);
  }
   */
  /**
   * Numeric representation of a month, with leading zeros
   */
  m() {
    return zerofill_2(this.getMonth());
  }
  /**
   * A short textual representation of a month, three letters
   * @param date
   */
  /*
  static M(date: Target){
   
  }
   
  M(){
   
  }
   */
  /**
   * Numeric representation of a month, without leading zeros
   */
  n() {
    return this.getMonth() + 1;
  }
  /**
   * Number of days in the given month
   */
  t() {
    return _Dates.t(this);
  }
  /**
   * The day of the year (starting from 0)
   */
  z() {
    return _Dates.z(this);
  }
  //////////////////////////////
  // Year
  //////////////////////////////
  /**
   * Whether it's a leap year
   */
  L() {
    return _Dates.L(this);
  }
  /**
   * ISO-8601 week-numbering year. This has the same value as Y, except that if the ISO week number (W) belongs to the previous or next year, that year is used instead.
   */
  o() {
    return this.getFullYear();
  }
  /**
   * A full numeric representation of a year, 4 digits
   */
  Y() {
    return this.getFullYear();
  }
  /**
   * A two digit representation of a year
   */
  y() {
    return String(this.getFullYear()).slice(2);
  }
  //////////////////////////////
  // Time
  //////////////////////////////
  /**
   * Lowercase Ante meridiem and Post meridiem
   */
  A() {
    return _Dates.A(this);
  }
  /**
   * B, what is it internet time ?
   */
  /**
   * 12-hour format of an hour without leading zeros
   */
  g() {
    return _Dates.g(this);
  }
  /**
   * 24-hour format of an hour without leading zeros
   */
  G() {
    return _Dates.G(this);
  }
  /**
   * Uppercase Ante meridiem and Post meridiem
   */
  a() {
    return _Dates.a(this);
  }
  /**
   * 12-hour format of an hour with leading zeros
   */
  h() {
    return _Dates.h(this);
  }
  /**
   * 24-hour format of an hour with leading zeros
   */
  H() {
    return _Dates.H(this);
  }
  /**
   * Minutes with leading zeros
   */
  i() {
    return _Dates.i(this);
  }
  /**
   * Seconds with leading zeros
   */
  s() {
    return _Dates.s(this);
  }
  /**
   * Microseconds
   * @example 654321
   */
  u() {
    return _Dates.u(this);
  }
  v() {
    return _Dates.v(this);
  }
  //////////////////////////////
  // Timezone
  //////////////////////////////
  // todo: e
  // todo: I
  // todo: O
  // todo: P
  // todo: T
  // todo: Z
  //////////////////////////////
  // Full Date/Time
  //////////////////////////////
  // todo: c
  // todo: r
  // todo: U
};
function zerofill_2(value) {
  return value < 10 ? "0" + value : value;
}

// src/js-dot/core/date/date-digit-units.ts
var DateDigitUnits = class {
  /**
   * Year
   */
  y;
  /**
   * Month
   */
  m;
  /**
   * Date
   */
  d;
  /**
   * Hour
   */
  h;
  /**
   * Minute
   */
  i;
  /**
   * Second
   */
  s;
  /**
   * Year to Month
   */
  ym;
  /**
   * Year to Date
   */
  ymd;
  /**
   * Year to Hour
   */
  ymdh;
  /**
   * Year to Minute
   */
  ymdhi;
  /**
   * Year to Second
   */
  ymdhis;
  constructor(date_or_parts) {
    const parts = date_or_parts instanceof DateParts ? date_or_parts : new DateParts(date_or_parts);
    const {
      y,
      m,
      d,
      h,
      i,
      s
    } = parts;
    const ym = y + m;
    const ymd = ym + d;
    const ymdh = ymd + h;
    const ymdhi = ymdh + i;
    const ymdhis = ymdhi + s;
    this.y = +y;
    this.m = +m;
    this.d = +d;
    this.h = +h;
    this.i = +i;
    this.s = +s;
    this.ym = +ym;
    this.ymd = +ymd;
    this.ymdh = +ymdh;
    this.ymdhi = +ymdhi;
    this.ymdhis = +ymdhis;
  }
};

// src/js-dot/core/date/date-string-units.ts
var DateStringUnits = class {
  y;
  m;
  d;
  h;
  i;
  ym;
  ymd;
  ymdh;
  ymdhi;
  constructor(date_or_parts) {
    const parts = date_or_parts instanceof DateParts ? date_or_parts : new DateParts(date_or_parts);
    const {
      y,
      m,
      d,
      h,
      i
    } = parts;
    const ym = `${y}-${m}`;
    const ymd = `${ym}-${d}`;
    const ymdh = `${ymd} ${h}`;
    const ymdhi = `${ymdh}:${i}`;
    this.y = y;
    this.m = m;
    this.d = d;
    this.h = h;
    this.i = i;
    this.ym = ym;
    this.ymd = ymd;
    this.ymdh = ymdh;
    this.ymdhi = ymdhi;
  }
};

// src/js-dot/core/date/date-units.ts
var DateUnits = class {
  digits;
  strings;
  constructor(date) {
    const parts = new DateParts(date);
    this.digits = new DateDigitUnits(parts);
    this.strings = new DateStringUnits(parts);
  }
};

// src/js-dot/core/date/date-unit-dates.ts
var DateUnitDates = class {
  static from(date_or_parts) {
    return new this(date_or_parts);
  }
  parts;
  y;
  ym;
  ymd;
  ymdh;
  ymdhi;
  ymdhis;
  constructor(date_or_parts, forEnd = false) {
    const parts = date_or_parts instanceof DateParts ? date_or_parts : new DateParts(date_or_parts);
    this.parts = parts;
    const {
      y,
      m,
      d,
      h,
      i,
      s
    } = parts;
    const fixedM = +m - 1;
    const fillValues = this.y = new Date(+y, 0, 1, 0, 0, 0, 0);
    this.ym = new Date(+y, fixedM, 1, 0, 0, 0, 0);
    this.ymd = new Date(+y, fixedM, +d, 0, 0, 0, 0);
    this.ymdh = new Date(+y, fixedM, +d, +h, 0, 0, 0);
    this.ymdhi = new Date(+y, fixedM, +d, +h, +i, 0, 0);
    this.ymdhis = new Date(+y, fixedM, +d, +h, +i, +s, 0);
  }
};

// src/js-dot/core/date/period-units.ts
var TIME_UNIT = {
  year: "year",
  month: "month",
  day: "day",
  hour: "hour",
  minute: "minute",
  second: "second"
};
var PeriodUnits = class {
  _yearAmount;
  _monthAmount;
  _condition;
  _test;
  constructor(condition) {
    condition = {
      yearDays: 365,
      monthDays: 30,
      yearFrom: 3,
      monthFrom: 6,
      dateFrom: 7,
      hourFrom: 86399,
      minuteFrom: 3599,
      ...condition
    };
    this._condition = condition;
    this._yearAmount = condition.yearDays * condition.yearFrom;
    this._monthAmount = condition.monthDays * condition.monthFrom;
    this._test = JSON.stringify(this._condition);
  }
  auto(period, unit) {
    if (unit) {
      return this.get(period, unit);
    }
    period = Period.from(period);
    const {
      days
    } = period;
    const begin = new DateUnitDates(period.begin);
    const end = new DateUnitDates(period.end);
    const {
      _condition
    } = this;
    const isYear = days >= this._yearAmount;
    if (_condition.year) {
      if (isYear) {
        return this.year(period);
      }
    }
    const isMonth = days >= this._monthAmount;
    if (_condition.month) {
      if (isYear || isMonth) {
        return this.month(period);
      }
    }
    const isDay = days >= _condition.dateFrom;
    if (_condition.day) {
      if (isYear || isMonth || isDay) {
        return this.day(period);
      }
    }
    const {
      durationSeconds
    } = period;
    const isHour = durationSeconds >= _condition.hourFrom;
    if (_condition.hour) {
      if (isYear || isMonth || isDay || isHour) {
        return this.hour(period);
      }
    }
    const isMinute = durationSeconds >= _condition.minuteFrom || begin.ymdh === end.ymdh && +begin.parts.i === 0 && +end.parts.i - +begin.parts.i > 10;
    if (_condition.minute) {
      if (isYear || isMonth || isDay || isHour || isMinute) {
        return this.minute(period);
      }
    }
    return this.second(period);
  }
  /**
   * Get Unit by unit string.
   * If unit does not exist, returns result of auto()
   * @param period
   * @param unit
   */
  get(period, unit) {
    if (typeof this[unit] !== "function") {
      throw RangeError(`Invalid Time Unit: ${unit}`);
    }
    return this[unit](period);
  }
  /**
   * Get Year's unit
   * @param period
   */
  year(period) {
    const target = this._condition.year;
    if (!target) {
      return this.month(period);
    }
    period = Period.from(period);
    return new PeriodUnit(TIME_UNIT.year, target, dateFormatted("Y-01-01T00:00:00.000", period.begin), dateFormatted("Y-12-30T23:59:59.999", period.end));
  }
  /**
   * Get Month's unit
   * @param period
   */
  month(period) {
    const target = this._condition.month;
    if (!target) {
      return this.day(period);
    }
    period = Period.from(period);
    return new PeriodUnit(TIME_UNIT.month, target, dateFormatted("Y-m-01T00:00:00.000", period.begin), dateFormatted("Y-m-30T23:59:59.999", period.end));
  }
  /**
   * Get Day's unit
   * @param period
   */
  day(period) {
    const target = this._condition.day;
    if (!target) {
      return this.hour(period);
    }
    period = Period.from(period);
    return new PeriodUnit(TIME_UNIT.day, target, dateFormatted("Y-m-dT00:00:00.000", period.begin), dateFormatted("Y-m-dT23:59:59.999", period.end));
  }
  /**
   * Get Hour's unit
   * @param period
   */
  hour(period) {
    const target = this._condition.hour;
    if (!target) {
      return this.minute(period);
    }
    period = Period.from(period);
    return new PeriodUnit(TIME_UNIT.hour, target, dateFormatted("Y-m-dTH:00:00.000", period.begin), dateFormatted("Y-m-dTH:59:59.999", period.end));
  }
  /**
   * Get Minute's unit
   * @param period
   */
  minute(period) {
    const target = this._condition.minute;
    if (!target) {
      return this.second(period);
    }
    period = Period.from(period);
    return new PeriodUnit(TIME_UNIT.minute, target, dateFormatted("Y-m-dTH:i:00.000", period.begin), dateFormatted("Y-m-dTH:i:59.999", period.end));
  }
  /**
   * Get Second's unit
   * @param period
   */
  second(period) {
    period = Period.from(period);
    return new PeriodUnit(TIME_UNIT.second, this._condition.second, period.begin, period.end);
  }
};
var PeriodUnit = class {
  constructor(unit, target, begin, end) {
    this.unit = unit;
    this.target = target;
    this.begin = begin;
    this.end = end;
  }
};
export { DATE_LAST_DATES, DateDigitUnits, DateParts, DateStringUnits, DateUnits, Dates, Period, PeriodUnit, PeriodUnits, TIME_UNIT, dateFormat, dateFormatted, dateModify, fromDateDigit, fromTimestamp, getDate, getDates, getLastDate2 as getLastDate, isLeapYear, toDateDigit, toYmdDigit };
